import { Alert, Button, Checkbox, Divider } from '@design-system';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  PaymentMethodCreateParams,
  SetupIntentResult,
  StripeError,
} from '@stripe/stripe-js';
import { customsCodeIsRequired } from '@utils/address';
import { useConvertCurrency } from '@utils/currencyConverter';
import { framedByDefault } from '@utils/frames';
import { normaliseShopifyPrice } from '@utils/prices';
import classNames from 'classnames';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import FrameSelectorDraw from '../../../../components/Containers/ReviewCheckoutDrawer/FrameSelectorDraw';
import { CustomsCode } from '../../../../components/Containers/ReviewCheckoutDrawer/PaymentMethod/CustomsCode';
import { PaymentMethodButton } from '../../../../components/Containers/ReviewCheckoutDrawer/PaymentMethod/PaymentMethodButton';
import { ReviewItem } from '../../../../components/Containers/ReviewCheckoutDrawer/ReviewItem';
import { ShippingAddressButton } from '../../../../components/Containers/ReviewCheckoutDrawer/ShippingAddress/ShippingAddressButton';
import { AML_LIMIT } from '../../../../constants/product';
import { pathFor } from '../../../../helpers/links';
import { useDialogContext } from '../../../../hooks/useDialogContext';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useAuth, useFlow } from '../../../../providers';
import { useDraws } from '../../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { useStore } from '../../../../store/utils';
import {
  GeneratedDrawType,
  GeneratedOrderPricingQuote,
} from '../../../../types/generated';
import { CurrencyCode } from '../../../../types/Locations';
import { TrackGtagEvent } from '../../../../utils/analytics';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';
import { AnalyticsFlowProvider } from '../../../../utils/analytics/flow';
import {
  captureBreadcrumb,
  captureError,
  captureErrorMessage,
} from '../../../../utils/capture';
import { drawEntryActions } from '../../store/draw-entry';
import { DrawWithFrames, PaymentType } from '../../types';
import { DrawEntryConfirmationDialog } from '../confirmation/DrawEntryConfirmationDialog';
import { ReviewDrawFooter } from './ReviewDrawFooter';
import { ReviewPricingInformation } from './ReviewPricingInformation';

type Props = {
  drawWithFrames: DrawWithFrames;
};

export const ReviewDrawer: React.FC<Props> = ({ drawWithFrames }) => {
  const { frames, product } = drawWithFrames;
  const drawController = usePreAuthDrawCtx();
  const { closeFlow, goToStep } = useFlow();
  const { hideDialog, showDialog } = useDialogContext();
  const dispatch = useDispatch();
  const { t } = useTranslation(['product', 'countries']);
  const [working, setWorking] = useState(false);
  const { user } = useAuth();
  const elements = useElements();
  const stripe = useStripe();
  const [emitter] = useAnalyticsEmitter();
  const [enterClicked, setEnterClicked] = useState(false);
  const [auctionTermsAccepted, setAuctionTermsAccepted] = useState(false);

  const isFramedByDefault = framedByDefault(product.framingOffered);

  const {
    activeDraw,
    addresses,
    bidValidationError,
    billingAddress,
    draw,
    entryIntentResponse,
    errorMessage,
    loading,
    newPaymentMethodUsingShippingAddress,
    optionalItems,
    paymentErrorCode,
    paymentMethods,
    selectedAddress: drawSelectedAddress,
    selectedPaymentMethodId,
    selectedPaymentType,
    setPaymentErrorCode,
    setupSecret,
    updating,
    usingNewPaymentMethod,
  } = drawController;

  const isPreOrder = useIsPreOrder(draw);

  const { addDrawEntryToSession, selectedDraws, updateDrawEntryStatus } =
    useDraws();

  const drawEntry = useStore((s) =>
    draw
      ? s.features.draw.drawEntry[draw.id]
      : { entering: false, entered: false },
  );

  const drawSupportsFrame = frames && frames.length > 0;

  const payWithCrypto = selectedPaymentType === PaymentType.CRYPTO;
  const paymentReady = Boolean(
    payWithCrypto || selectedPaymentMethodId || usingNewPaymentMethod,
  );
  const currencyConverter = useConvertCurrency();
  const workingOrEntering = working || Boolean(drawEntry?.entering);
  const disableEditing = workingOrEntering;
  const drawStale = updating || loading(activeDraw?.draw.id || '');
  const drawDataReady = entryIntentResponse?.readyToConfirm && paymentReady;

  const eventProperties = useMemo(
    () => ({
      'type': draw?.drawType,
      'artist_id': product.artistIds[0],
      ...(isPreOrder
        ? {
            'preorder_application_count': 1,
            'preorder_intent_id': entryIntentResponse?.intent?.id,
            'public_preorder_id': draw?.id,
          }
        : {
            'draw_entry_count': 1,
            'draw_intent_id': entryIntentResponse?.intent?.id,
            'public_draw_id': draw?.id,
            'bid': entryIntentResponse?.intent?.bid,
            'isFramed': optionalItems.length > 0,
          }),
      'product_id': product.id,
      'pre-order opt in':
        !!draw?.preorderEnabled && drawController.isPreOrderSelected,
      'product_name': product.title,
      'user_email': user?.email,
      'user_id': user?.id,
    }),
    [
      draw?.drawType,
      draw?.id,
      draw?.preorderEnabled,
      drawController.isPreOrderSelected,
      entryIntentResponse?.intent?.bid,
      entryIntentResponse?.intent?.id,
      isPreOrder,
      optionalItems.length,
      product.artistIds,
      product.id,
      product.title,
      user?.email,
      user?.id,
    ],
  );

  const selectedAddress = addresses.find((a) => a.id === drawSelectedAddress);

  const isCustomsCodeMissing =
    customsCodeIsRequired(selectedAddress?.countryCode) &&
    !selectedAddress?.customsCode;

  const readyToConfirmDraw =
    drawDataReady && !drawStale && !isCustomsCodeMissing;

  useEffect(() => {
    if (drawEntry?.entered && enterClicked) {
      closeFlow();
      updateDrawEntryStatus(draw?.id);
      addDrawEntryToSession(entryIntentResponse);

      let isAmlRequired = false;
      if (entryIntentResponse.pricing?.pricing) {
        const total = normaliseShopifyPrice(
          entryIntentResponse.pricing?.pricing?.totalPrice,
          entryIntentResponse.pricing.pricing.currencyCode as CurrencyCode,
        );

        isAmlRequired = Boolean(
          total >
          currencyConverter.convert(
            AML_LIMIT,
            entryIntentResponse.pricing.pricing.currencyCode,
          ),
        );
      }

      // if this is a multi-draw, we don't want to show the success dialog yet
      // unless only one draw is selected
      if (!draw?.multisetID || selectedDraws.length <= 1) {
        showDialog(
          <AnalyticsFlowProvider
            name={isPreOrder ? 'pre-order success' : 'draw success'}
            properties={{
              'payment method': selectedPaymentType,
              'type': draw?.drawType,
            }}
          >
            {() => (
              <DrawEntryConfirmationDialog
                backgroundImage={
                  product.thumbnail?.url
                    ? {
                        filename: product.thumbnail.url,
                        alt: product.thumbnail.alt || '',
                        name: '',
                        title: '',
                        id: 1,
                        copyright: '',
                        fieldtype: '',
                      }
                    : null
                }
                bid={entryIntentResponse.intent?.bid}
                isAmlRequired={isAmlRequired}
                isPreOrder={isPreOrder}
                isPreOrderOptIn={Boolean(entryIntentResponse.intent?.preOrder)}
                onClose={hideDialog}
                productTitle={product.title}
              />
            )}
          </AnalyticsFlowProvider>,
        );
      }

      emitter(isPreOrder ? 'application' : 'entry', 'success', eventProperties);

      TrackGtagEvent(
        isPreOrder ? 'preorder_application' : 'draw_entry',
        eventProperties,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawEntry?.entered]);

  function handleStripeResponse(
    response: SetupIntentResult | { error: StripeError },
  ) {
    if (draw && entryIntentResponse?.intent && !response.error) {
      setPaymentErrorCode(null);

      // Handle successful setup intent
      dispatch(
        drawEntryActions.setupDrawEntry({
          actionId: draw.id,
          drawEntryIntentId: entryIntentResponse.intent.id,
          polling: true,
          freshEntry: true,
          entering: true,
          entered: false,
        }),
      );

      return;
    }

    const { error } = response;
    if (error) {
      setPaymentErrorCode(error.code || error.type);
      console.error(error);
    }
  }

  async function handleDrawEntry() {
    setEnterClicked(true);
    captureBreadcrumb({
      level: 'info',
      message: `${user?.id} has clicked "Enter Draw"`,
      category: 'draw',
    });

    if (!stripe || !elements) {
      captureErrorMessage('Stripe SDK is not setup');

      return;
    }

    emitter('submit', 'clicked');
    setPaymentErrorCode(null);
    if (payWithCrypto) {
      goToStep('crypto_com');
      return;
    }

    setWorking(true);
    try {
      const url = new URL(window.location.href);

      if (entryIntentResponse?.intent) {
        url.searchParams.set('dei', entryIntentResponse.intent.id);
      }

      // This means we're using an existing payment method, so we just need to pass in
      // the setup secret returned from the backend to confirm the setup intent using
      // that payment method.
      if (selectedPaymentMethodId) {
        const response = await stripe.confirmSetup({
          clientSecret: setupSecret || '',
          confirmParams: {
            return_url: url.toString(),
          },
          redirect: 'if_required',
        });

        handleStripeResponse(response);

        return;
      }

      // This means we're creating a payment method with the SCA action. So instead of
      // passing in the client secret we've setup on the payable, we want to pass in
      // the payment elements SDK. This is used to fetch the tokenized card data, and then
      // confirm the setup intent using that data.
      // eslint-disable-next-line prettier/prettier
      let billingDetails: PaymentMethodCreateParams.BillingDetails | undefined =
        void 0;

      // If the follow flag is set to `true` then we need to copy the shipping address
      // across to the setup intent confirmation options. Otherwise the Stripe Elements
      // SDK will pull through the billing address that has been entered on the "Add
      // Payment Element" with the Stripe Address Element.
      if (newPaymentMethodUsingShippingAddress) {
        if (!selectedAddress) throw new Error('No shipping address selected');

        billingDetails = {
          // eslint-disable-next-line prettier/prettier
          name: [selectedAddress.firstName, selectedAddress.lastName].join(' '),
          phone: selectedAddress.phone ?? void 0,
          email: user?.email ?? '',
          address: {
            // None of these fields will be undefined, we should fix the types for this!
            line1: selectedAddress.addressLine1 ?? '',
            line2: selectedAddress.addressLine2 ?? '',
            city: selectedAddress.city ?? '',
            country: selectedAddress.countryCode ?? '',
            postal_code: selectedAddress.zip ?? '',
            state: selectedAddress.provinceName ?? '',
          },
        };
      } else {
        const storedBillingAddress = billingAddress;

        billingDetails = {
          name: storedBillingAddress?.name ?? '',
          phone: storedBillingAddress?.phone ?? '',
          email: user?.email ?? '',
          address: {
            line1: storedBillingAddress?.address?.line1 ?? '',
            line2: storedBillingAddress?.address?.line2 ?? '',
            city: storedBillingAddress?.address?.city ?? '',
            country: storedBillingAddress?.address?.country ?? '',
            postal_code: storedBillingAddress?.address?.postal_code ?? '',
            state: storedBillingAddress?.address?.state ?? '',
          },
        };
      }

      const response = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: url.toString(),
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: 'if_required',
      });

      handleStripeResponse(response);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="flex flex-col space-y-6">
      {errorMessage && (
        <div className="w-full flex-1">
          <Alert status="error">
            {typeof errorMessage === 'boolean'
              ? bidValidationError
                ? t('auction.default.accountBidTooLowGeneric')
                : t('product:drawers.review.draw.failureMessageGeneric')
              : errorMessage}
          </Alert>
        </div>
      )}

      <div className="flex w-full flex-col space-y-4">
        <div
          className={classNames(
            'flex flex-col space-y-4 rounded-sm border border-divider/20 p-4',
            { 'cursor-not-allowed bg-neutral-1': disableEditing },
          )}
        >
          <ReviewItem
            allowMultiples={false}
            artist={product.artists[0]?.name ?? ''}
            disabled={disableEditing}
            // TODO: check this
            imageFocus=""
            // TODO: check this
            imageUrl={product.thumbnail?.url || ''}
            price={
              draw?.drawType === GeneratedDrawType.Auction
                ? undefined
                : product.price
            }
            quantity={1}
            title={product.title}
          />
          {drawSupportsFrame && !isFramedByDefault ? (
            <>
              <Divider />
              <FrameSelectorDraw
                currencyCode={
                  draw?.auctionReservePrice?.currencyCode as CurrencyCode
                }
                disabled={disableEditing}
                draw={drawWithFrames}
                setVisibleForm={(formType) => {
                  if (formType === 'frame') {
                    emitter('framing', 'clicked', {
                      'product frame chosen': optionalItems.length > 0,
                    });
                  }
                  switch (formType) {
                    case 'locale':
                      goToStep('locale');
                      break;
                    case 'frame':
                      goToStep('frame');
                      break;
                    default:
                      goToStep('apply');
                  }
                }}
              />
            </>
          ) : null}
        </div>

        <div className="flex flex-col space-y-2">
          <div className="text-xs">
            {t('product:drawers.review.draw.shipTo')}
          </div>
          <ShippingAddressButton
            addresses={addresses}
            disabled={disableEditing}
            onChange={(edit) => {
              if (!edit) {
                goToStep('add_shipping_address');
              } else {
                goToStep('select_shipping');
              }
            }}
            selectedAddressId={drawSelectedAddress}
          />
        </div>

        <CustomsCode
          address={selectedAddress}
          onClick={() => {
            goToStep('edit_shipping_address');
          }}
        />

        <div className="flex flex-col space-y-2">
          <div className="text-xs">
            {t('product:drawers.review.draw.payWith')}
          </div>
          <PaymentMethodButton
            disabled={disableEditing}
            isCryptoComEnabled={draw?.cryptoComEnabled || false}
            isPreOrder={isPreOrder}
            paymentErrorCode={paymentErrorCode}
            paymentMethods={paymentMethods || []}
            selectedPaymentMethodId={selectedPaymentMethodId || undefined}
            selectedPaymentType={selectedPaymentType}
            usingNewPaymentMethod={usingNewPaymentMethod}
          />
        </div>

        {entryIntentResponse.pricing && (
          <ReviewPricingInformation
            bid={entryIntentResponse.intent?.bid}
            isPreOrder={isPreOrder}
            paymentReady={paymentReady}
            payWithCrypto={payWithCrypto}
            pricingBreakdown={
              entryIntentResponse.pricing as GeneratedOrderPricingQuote
            }
            updating={updating}
          />
        )}

        {drawController.draw?.preorderEnabled && !isPreOrder ? (
          <div className="flex flex-col gap-4 rounded-sm border border-divider/20 p-4">
            <div>
              <p className="font-bold">
                {t('product:drawers.review.draw.preOrderOptIn.title')}
              </p>
              <p>
                {t('product:drawers.review.draw.preOrderOptIn.description')}
              </p>
            </div>
            <Checkbox
              checked={drawController.isPreOrderSelected}
              disabled={loading(activeDraw?.draw.id || '')}
              id="pre-order-opt-in"
              label={t('product:drawers.review.draw.preOrderOptIn.label')}
              name="pre-order-opt-in"
              onChange={(e) => {
                drawController.setIsPreOrderSelected(e.currentTarget.checked);
              }}
              type="checkbox"
            />
          </div>
        ) : null}

        {draw?.drawType === GeneratedDrawType.Auction && (
          <Checkbox
            checked={auctionTermsAccepted}
            data-testid="field-accept-auction-terms"
            disabled={workingOrEntering}
            id="accept-auction-terms"
            label={
              (
                <Trans i18nKey="product:auction.auctionTermsCheckbox" t={t}>
                  I authorise Avant Arte to charge the confirmed amount if I win
                  and agree to the auction&nbsp;
                  <Link
                    className="hyperlink"
                    href={pathFor({
                      page: 'SUPPORT',
                      slug: 'terms-and-conditions',
                      anchor: 'auctions',
                    })}
                    prefetch={false}
                    target="_blank"
                  >
                    terms and conditions
                  </Link>
                  .
                </Trans>
              ) as unknown as string
            }
            name="accept-auction-terms"
            onChange={(e) => {
              setAuctionTermsAccepted(e.currentTarget.checked);
            }}
            type="checkbox"
            value="accept-auction-terms"
          />
        )}

        <div className="!mt-6 flex flex-col space-y-3">
          <Button
            disabled={
              !readyToConfirmDraw ||
              (draw?.drawType === GeneratedDrawType.Auction &&
                !auctionTermsAccepted)
            }
            fullWidth
            label={
              isPreOrder
                ? t('product:drawers.review.preOrderButton')
                : t('product:drawers.review.draw.enterButton')
            }
            loading={workingOrEntering}
            onClick={() => {
              handleDrawEntry().catch((error) => {
                console.error(error);
                captureError(error as Error);
              });
            }}
            size="lg"
            variant="primary"
          />
        </div>
        {readyToConfirmDraw && (
          <ReviewDrawFooter
            isPreOrderOptIn={drawController.isPreOrderSelected}
            payWithCrypto={payWithCrypto}
            // TODO: this should work but check
            productTags={product.tags}
            productTitle={product.title}
          />
        )}
      </div>
    </div>
  );
};
